"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { cn } from "@/utils";
import { brands } from "./brandsData";

const links = [
  { label: "Home", href: "/" },
  { label: "Bundle", href: "/bundle" },
  { label: "Blog", href: "/blog" },
  { label: "Services", href: "/wordpress-white-label-service" },
  { label: "Contact", href: "/contact" },
];

const linkBase =
  "jlt-rounded-full jlt-px-[15px] jlt-py-[9px] jlt-text-[14.5px] jlt-font-medium jlt-transition-colors jlt-duration-200";

const Nav = () => {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <nav className="jlt-sticky jlt-top-[14px] jlt-z-[200] jlt-px-[clamp(20px,5vw,72px)]">
      <div
        className={cn(
          "jlt-mx-auto jlt-flex jlt-items-center jlt-justify-between jlt-gap-[18px] jlt-rounded-full jlt-border jlt-border-pl-line jlt-backdrop-blur-[18px] jlt-backdrop-saturate-150 jlt-transition-[max-width,padding,box-shadow,background] jlt-duration-500 jlt-ease-spring",
          scrolled
            ? "jlt-max-w-[880px] jlt-bg-white/[.94] jlt-py-[6px] jlt-pl-4 jlt-pr-2 jlt-shadow-pl"
            : "jlt-max-w-[1080px] jlt-bg-white/[.74] jlt-py-[10px] jlt-pl-5 jlt-pr-3 jlt-shadow-pl-sm"
        )}
      >
        <Link href="/" className="jlt-flex jlt-items-center jlt-gap-[11px]">
          <Image
            src="/logo.svg"
            alt="Pixar Labs"
            width={140}
            height={30}
            priority
            className={cn(
              "jlt-w-auto jlt-transition-[height] jlt-duration-500 jlt-ease-spring",
              scrolled ? "jlt-h-[26px]" : "jlt-h-[30px]"
            )}
          />
        </Link>

        {/* desktop links */}
        <div className="jlt-hidden jlt-items-center jlt-gap-[2px] min-[840px]:jlt-flex">
          <Link
            href="/"
            className={cn(
              linkBase,
              pathname === "/"
                ? "jlt-bg-pl-ink jlt-text-white"
                : "jlt-text-pl-ink2 hover:jlt-bg-pl-bg hover:jlt-text-pl-ink"
            )}
          >
            Home
          </Link>

          {/* brands mega menu */}
          <div className="jlt-group/mega jlt-relative">
            <Link
              href="/#brands"
              className={cn(
                linkBase,
                "jlt-inline-flex jlt-items-center jlt-gap-[7px] jlt-text-pl-ink2 hover:jlt-bg-pl-bg hover:jlt-text-pl-ink"
              )}
            >
              Brands
              <span className="jlt-h-[7px] jlt-w-[7px] jlt-translate-y-[-1px] jlt-rotate-45 jlt-border-b-[1.6px] jlt-border-r-[1.6px] jlt-border-current jlt-opacity-50 jlt-transition-transform jlt-duration-300 jlt-ease-spring group-hover/mega:jlt-translate-y-[2px] group-hover/mega:jlt-rotate-[225deg]" />
            </Link>

            <div className="jlt-invisible jlt-absolute jlt-left-1/2 jlt-top-[calc(100%+14px)] jlt-grid jlt-w-[580px] -jlt-translate-x-1/2 jlt-translate-y-3 jlt-scale-95 jlt-grid-cols-2 jlt-gap-1 jlt-rounded-3xl jlt-border jlt-border-pl-line jlt-bg-white jlt-p-[14px] jlt-opacity-0 jlt-shadow-pl-lg jlt-transition-[opacity,transform,visibility] jlt-duration-300 jlt-ease-spring group-hover/mega:jlt-visible group-hover/mega:jlt-translate-y-0 group-hover/mega:jlt-scale-100 group-hover/mega:jlt-opacity-100">
              <span
                aria-hidden="true"
                className="jlt-absolute jlt-inset-x-0 jlt--top-4 jlt-h-4"
              />
              {brands.map((brand) => (
                <Link
                  key={brand.name}
                  href={brand.href}
                  target={brand.target}
                  rel={brand.target === "_blank" ? "noopener noreferrer" : undefined}
                  className="jlt-flex jlt-items-center jlt-gap-3 jlt-rounded-2xl jlt-px-3 jlt-py-[11px] jlt-text-pl-ink jlt-transition-[background,transform] jlt-duration-200 jlt-ease-spring hover:jlt-bg-pl-bg hover:jlt-translate-x-[3px]"
                >
                  <span className="jlt-grid jlt-h-[38px] jlt-w-[38px] jlt-flex-none jlt-place-items-center jlt-rounded-blob2 jlt-border jlt-border-pl-line jlt-bg-white">
                    {brand.logo}
                  </span>
                  <span>
                    <b className="jlt-block jlt-font-display jlt-text-[14px] jlt-leading-[1.2]">
                      {brand.name}
                    </b>
                    <i className="jlt-not-italic jlt-text-[12px] jlt-text-pl-mute">
                      {brand.tagline}
                    </i>
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {links.slice(1).map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={cn(
                linkBase,
                pathname?.startsWith(link.href)
                  ? "jlt-bg-pl-ink jlt-text-white"
                  : "jlt-text-pl-ink2 hover:jlt-bg-pl-bg hover:jlt-text-pl-ink"
              )}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="jlt-flex jlt-items-center jlt-gap-2">
          <Link
            href="/account/login"
            className="jlt-inline-flex jlt-items-center jlt-justify-center jlt-rounded-full jlt-bg-pl-blue jlt-px-5 jlt-py-[10px] jlt-text-[15px] jlt-font-semibold jlt-text-white jlt-shadow-[0_10px_26px_-10px_rgba(42,76,255,.55)] jlt-transition-transform jlt-duration-200 jlt-ease-spring hover:-jlt-translate-y-[2px]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-user-icon lucide-user jlt-mr-1"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <span>Account</span>
          </Link>

          {/* mobile toggle */}
          <button
            type="button"
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="jlt-grid jlt-h-10 jlt-w-10 jlt-place-items-center jlt-rounded-full jlt-border jlt-border-pl-line jlt-bg-white jlt-text-pl-ink min-[840px]:jlt-hidden"
          >
            <span className="jlt-relative jlt-block jlt-h-[14px] jlt-w-[18px]">
              <span
                className={cn(
                  "jlt-absolute jlt-left-0 jlt-h-[2px] jlt-w-full jlt-rounded jlt-bg-current jlt-transition-all jlt-duration-300",
                  open ? "jlt-top-[6px] jlt-rotate-45" : "jlt-top-0"
                )}
              />
              <span
                className={cn(
                  "jlt-absolute jlt-left-0 jlt-top-[6px] jlt-h-[2px] jlt-w-full jlt-rounded jlt-bg-current jlt-transition-opacity jlt-duration-300",
                  open ? "jlt-opacity-0" : "jlt-opacity-100"
                )}
              />
              <span
                className={cn(
                  "jlt-absolute jlt-left-0 jlt-h-[2px] jlt-w-full jlt-rounded jlt-bg-current jlt-transition-all jlt-duration-300",
                  open ? "jlt-top-[6px] -jlt-rotate-45" : "jlt-top-[12px]"
                )}
              />
            </span>
          </button>
        </div>
      </div>

      {/* mobile panel */}
      {open ? (
        <div className="jlt-mx-auto jlt-mt-3 jlt-max-w-[1080px] jlt-rounded-3xl jlt-border jlt-border-pl-line jlt-bg-white jlt-p-4 jlt-shadow-pl-lg min-[840px]:jlt-hidden">
          <div className="jlt-grid jlt-gap-1">
            {links.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="jlt-rounded-2xl jlt-px-4 jlt-py-3 jlt-text-[15px] jlt-font-semibold jlt-text-pl-ink hover:jlt-bg-pl-bg"
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="jlt-mt-3 jlt-border-t jlt-border-pl-line jlt-pt-3">
            <span className="jlt-mb-2 jlt-block jlt-px-4 jlt-text-[11.5px] jlt-font-extrabold jlt-uppercase jlt-tracking-[.07em] jlt-text-pl-mute">
              Brands
            </span>
            <div className="jlt-grid jlt-gap-1">
              {brands.map((brand) => (
                <Link
                  key={brand.name}
                  href={brand.href}
                  target={brand.target}
                  rel={brand.target === "_blank" ? "noopener noreferrer" : undefined}
                  className="jlt-flex jlt-items-center jlt-gap-3 jlt-rounded-2xl jlt-px-4 jlt-py-2 jlt-text-pl-ink hover:jlt-bg-pl-bg"
                >
                  <span className="jlt-grid jlt-h-8 jlt-w-8 jlt-flex-none jlt-place-items-center jlt-rounded-blob2 jlt-border jlt-border-pl-line jlt-bg-white">
                    {brand.logo}
                  </span>
                  <span className="jlt-font-display jlt-text-[14px] jlt-font-bold">
                    {brand.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </nav>
  );
};

export default Nav;
